/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, GridColumn as Column, GridCustomCellProps, GridToolbar } from '@progress/kendo-react-grid';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { CSVLink } from 'react-csv';
import { process, State, CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import '@progress/kendo-theme-default/dist/all.css';
import axios from 'axios';
import { DashboardPageProps } from './types';
import { getConfig } from '../../Config';
import { Participant } from '../../models/dashboardTypes';
import { formatDate } from '../../utils/dateUtils';
import { processParticipantSessions } from '../../utils/sessionProcessing';
import { MAX_SESSIONS } from '../../constants/sessions';
import './Dashboard.scss';

interface ExtendedParticipant extends Participant {
  [key: string]: unknown;
  searchableSessionText: string;
}

export interface SessionStatus {
  status: string;
  subtitle?: string | null;
}

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const initialState: State = {
  skip: 0,
  take: 20,
  sort: [],
  filter: undefined,
};

export interface SessionWindow {
  openDate: Date;
  dueDate: Date;
}

// Quarterly session window (open/due dates) for a session, anchored on the activation date.
// Single source of the session-window date math; consumed by calculateSessionStatus and by
// the offboard-placement logic in sessionProcessing.
export const getSessionWindow = (activationDateUnix: number, sessionNumber: number): SessionWindow => {
  const activationDate = new Date(activationDateUnix * 1000);
  const dueDate = new Date(activationDate);
  const openDate = new Date(activationDate);

  const monthsToAdd = (sessionNumber - 1) * 3;
  const dueDaysToAdd = 45;
  const openDaysToSubtract = 10;

  const targetYear = dueDate.getFullYear() + Math.floor((dueDate.getMonth() + monthsToAdd) / 12);
  const targetMonth = (dueDate.getMonth() + monthsToAdd) % 12;
  const originalDay = dueDate.getDate();

  const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
  const adjustedDay = Math.min(originalDay, lastDayOfTargetMonth);

  dueDate.setFullYear(targetYear, targetMonth, adjustedDay);
  dueDate.setDate(dueDate.getDate() + dueDaysToAdd);

  if (sessionNumber === 1) {
    openDate.setTime(activationDate.getTime());
  } else {
    openDate.setFullYear(targetYear, targetMonth, adjustedDay);
    openDate.setDate(openDate.getDate() - openDaysToSubtract);
  }

  return { openDate, dueDate };
};

export const calculateSessionStatus = (participant: Participant, sessionNumber: number): SessionStatus => {
  if (participant.registrationState === 'OFFBOARD') {
    const offboardDate = participant.offboardedOn ? new Date((participant.offboardedOn as number) * 1000) : null;
    const now = new Date();

    if (offboardDate) {
      const formattedDate = formatDate(offboardDate);
      return {
        status: offboardDate > now ? 'Rejoin until' : 'Offboarded',
        subtitle: formattedDate,
      };
    }
    return { status: 'Offboarded', subtitle: null };
  }

  if (participant.activatedOn) {
    const { openDate, dueDate } = getSessionWindow(participant.activatedOn as number, sessionNumber);

    const isMissed = dueDate <= new Date();
    const status = isMissed ? 'Missed' : 'Due';
    let subtitle: string;
    if (isMissed) {
      subtitle = formatDate(dueDate);
    } else {
      subtitle = `Open on ${formatDate(openDate)}\nDue on ${formatDate(dueDate)}`;
    }

    return {
      status,
      subtitle,
    };
  }

  return { status: '' };
};

const Dashboard: React.FC<DashboardPageProps> = ({ isAuthReady }: DashboardPageProps) => {
  const navigate = useNavigate();
  const [dataState, setDataState] = useState<State>(initialState);
  const [sampleData, setData] = useState<ExtendedParticipant[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({
    logic: 'or',
    filters: [],
  });

  const debouncedSearchValue = useDebounce(searchValue, 300);

  const handleFilterChange = (event: InputChangeEvent) => {
    const value = event.value || '';
    setSearchValue(value);
  };

  useEffect(() => {
    // Reset paging to first page when filter changes
    setDataState((prevState) => ({
      ...prevState,
      skip: 0,
    }));

    if (!debouncedSearchValue.trim()) {
      setFilter({
        logic: 'or',
        filters: [],
      });
      return;
    }

    const searchWords = debouncedSearchValue.toLowerCase().trim().split(/\s+/);
    const customFilter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: searchWords.map((word) => ({
        logic: 'or',
        filters: [
          { field: 'id', operator: 'contains', value: word },
          { field: 'siteID', operator: 'contains', value: word },
          { field: 'participantUID', operator: 'contains', value: word },
          {
            field: 'searchableSessionText',
            operator: 'contains',
            value: word,
          },
        ],
      })),
    };

    setFilter(customFilter);
  }, [debouncedSearchValue]);

  // Custom cell for session status color indication
  const SessionStatusCell = ({ dataItem, field }: { dataItem: ExtendedParticipant; field?: string }) => {
    if (!field) return <td></td>;

    const cellValue = dataItem[field] as SessionStatus | undefined;

    if (!cellValue || cellValue.status === '') {
      return <td></td>;
    }

    if (typeof cellValue === 'object' && cellValue.status) {
      const { status, subtitle } = cellValue;

      let colorClass = '';
      switch (status.toLowerCase()) {
        case 'completed':
          colorClass = 'session-completed';
          break;
        case 'due':
          colorClass = 'session-due';
          break;
        case 'missed':
          colorClass = 'session-missed';
          break;
        case 'offboarded':
        case 'rejoin until':
          colorClass = 'session-offboarded';
          break;
      }

      return (
        <td className={`session-status-cell ${colorClass}`}>
          {status.toLowerCase() !== 'due' && status}
          {subtitle && (
            <>
              {status.toLowerCase() !== 'due' && <br />}
              {subtitle?.split('\n').map((line, index) => (
                <span key={index}>
                  {index > 0 && <br />}
                  <small>{line}</small>
                </span>
              ))}
            </>
          )}
        </td>
      );
    }

    // Fallback for any unexpected data
    return <td>Unknown data</td>;
  };

  // Custom cell for clickable Participant ID with lookup icon
  const ParticipantIDCell = (props: GridCustomCellProps) => {
    const participantId = props.dataItem[props.field || ''];
    const participantUID = props.dataItem.participantUID;

    const handleClick = () => {
      if (participantUID) {
        navigate(`/participant/${participantUID}`);
      }
    };

    return (
      <td className="participant-id-cell">
        <span>{participantId}</span>
        <button
          onClick={handleClick}
          className="participant-id-linkout-button"
          title={`View details for ${participantId}`}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15,3 21,3 21,9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </button>
      </td>
    );
  };

  useEffect(() => {
    if (!isAuthReady) return;
    const config = getConfig();
    const path = `${config.apiEndpoint}/participants`;
    axios
      .get(path)
      .then(function (res) {
        const processedData = res.data.value.map((item: Participant): ExtendedParticipant => {
          const sessionEndDates = processParticipantSessions(item);

          const dates = {
            createdOn: new Date((item.createdOn as number) * 1000),
            activatedOn: item.activatedOn ? new Date((item.activatedOn as number) * 1000) : null,
            offboardRequestedOn: item.offboardRequestedOn
              ? new Date((item.offboardRequestedOn as number) * 1000)
              : undefined,
            offboardedOn: item.offboardedOn ? new Date((item.offboardedOn as number) * 1000) : undefined,
          };

          const searchableSessionText = Object.values(sessionEndDates)
            .filter((session) => session.status !== '')
            .map((session) => `${session.status} ${session.subtitle || ''}`)
            .join(' ')
            .toLowerCase();

          const sessionSortValues: { [key: string]: number } = {};
          for (let i = 0; i < MAX_SESSIONS; i++) {
            const sessionNumber = i + 1;
            const session = sessionEndDates[sessionNumber];

            let sortValue: number;
            if (!session || session.status === '') {
              sortValue = -9999999999999; // Push empty sessions to bottom
            } else if (session.subtitle) {
              const parsedDate = new Date(session.subtitle);
              sortValue = isNaN(parsedDate.getTime()) ? new Date().getTime() : parsedDate.getTime();
            } else {
              sortValue = new Date().getTime();
            }

            sessionSortValues[`session${sessionNumber}Sort`] = sortValue;
          }

          const registrationDateSort = dates.createdOn ? dates.createdOn.getTime() : -9999999999999;
          const activationDateSort = dates.activatedOn ? dates.activatedOn.getTime() : -9999999999999;

          return {
            ...item,
            ...sessionEndDates,
            ...sessionSortValues,
            ...dates,
            searchableSessionText,
            createdOnSort: registrationDateSort,
            activatedOnSort: activationDateSort,
          } as ExtendedParticipant;
        });

        setData(processedData);
      })
      .catch(function (err) {
        console.log(err);
      });
  }, [isAuthReady]);

  // Prepare CSV data from the current filtered data
  const prepareCsvData = () => {
    const filteredData = filterBy(sampleData, filter);

    return filteredData.map((item: ExtendedParticipant) => {
      const csvRow: Record<string, unknown> = {
        'Participant ID': item.id,
        'Site ID': item.siteID || '',
        'Participant UID': item.participantUID || '',
        Age: item.age || '',
        Gender: item.gender || '',
        Diagnosis: item.diagnosis || '',
        'ApoE Status': item.apoEStatus || '',
        'Amyloid Status': item.amyloidStatus || '',
        'Education Years': item.educationYears || '',
        'Registration Date': item.createdOn instanceof Date ? formatDate(item.createdOn) : '',
        'Activation Date': item.activatedOn instanceof Date ? formatDate(item.activatedOn) : '',
      };

      for (let i = 1; i <= MAX_SESSIONS; i++) {
        const sessionValue = item[i] as SessionStatus | undefined;
        if (sessionValue && typeof sessionValue === 'object' && 'status' in sessionValue) {
          const { status, subtitle } = sessionValue;
          csvRow[`Session ${i}`] = subtitle ? `${status} ${subtitle}` : status;
        } else {
          csvRow[`Session ${i}`] = '';
        }
      }

      return csvRow;
    });
  };

  const csvHeaders = [
    { label: 'Participant ID', key: 'Participant ID' },
    { label: 'Site ID', key: 'Site ID' },
    { label: 'Participant UID', key: 'Participant UID' },
    { label: 'Age', key: 'Age' },
    { label: 'Gender', key: 'Gender' },
    { label: 'Diagnosis', key: 'Diagnosis' },
    { label: 'ApoE Status', key: 'ApoE Status' },
    { label: 'Amyloid Status', key: 'Amyloid Status' },
    { label: 'Education Years', key: 'Education Years' },
    { label: 'Registration Date', key: 'Registration Date' },
    { label: 'Activation Date', key: 'Activation Date' },
    ...Array.from({ length: MAX_SESSIONS }, (_, index) => ({
      label: `Session ${index + 1}`,
      key: `Session ${index + 1}`,
    })),
  ];

  const processedGridData = React.useMemo(() => {
    if (sampleData.length === 0) {
      return { data: [], total: 0 };
    }

    const filteredData = filter.filters.length > 0 ? filterBy(sampleData, filter) : sampleData;

    let sortedData = filteredData;
    if (dataState.sort && dataState.sort.length > 0) {
      const sortDescriptor = dataState.sort[0];
      const { field, dir } = sortDescriptor;

      if (field && field.endsWith('Sort')) {
        sortedData = [...filteredData].sort((a, b) => {
          const aValue = (a as ExtendedParticipant)[field] as number;
          const bValue = (b as ExtendedParticipant)[field] as number;

          if (aValue < 0 && bValue < 0) {
            return 0;
          }

          if (aValue < 0 && bValue >= 0) {
            return 1;
          }

          if (aValue >= 0 && bValue < 0) {
            return -1;
          }

          if (dir === 'asc') {
            return aValue - bValue;
          } else {
            return bValue - aValue;
          }
        });

        const processed = process(sortedData, { ...dataState, sort: [] });
        return processed;
      }
    }

    const processed = process(sortedData, dataState);
    return processed;
  }, [sampleData, filter, dataState]);

  return (
    <div className="dashboard-container">
      <h2>Dashboard</h2>

      <div className="dashboard-grid-container">
        <Grid
          key="dashboard-grid"
          data={processedGridData}
          {...dataState}
          pageable={{ buttonCount: 5, pageSizes: [10, 20, 25] }}
          sortable={true}
          onDataStateChange={(e) => setDataState(e.dataState)}
        >
          <GridToolbar>
            <div className="dashboard-toolbar">
              <div className="dashboard-grid-filter">
                <Input value={searchValue} onChange={handleFilterChange} placeholder="Search table..." />
                <CSVLink
                  data={prepareCsvData()}
                  headers={csvHeaders}
                  filename={`dashboard-data-${new Date().toISOString().split('T')[0]}.csv`}
                  className="dashboard-button"
                >
                  Download CSV
                </CSVLink>
              </div>
              <div className="dashboard-button-group">
                <a onClick={() => navigate('/register-participant')} className="dashboard-button">
                  + Add new participant
                </a>
              </div>
            </div>
          </GridToolbar>
          <Column field="id" title="Participant ID" width="280px" cells={{ data: ParticipantIDCell }} />
          <Column
            field="createdOnSort"
            title="Registration Date"
            width="125px"
            format="{0:dd MMM yyyy}"
            cells={{ data: (props) => <td>{props.dataItem.createdOn ? formatDate(props.dataItem.createdOn) : ''}</td> }}
          />
          <Column
            field="activatedOnSort"
            title="Activation Date"
            width="110px"
            format="{0:dd MMM yyyy}"
            cells={{
              data: (props) => <td>{props.dataItem.activatedOn ? formatDate(props.dataItem.activatedOn) : ''}</td>,
            }}
          />
          <Column field="siteID" title="Site ID" width="120px" />
          {Array.from({ length: MAX_SESSIONS }, (_, index) => (
            <Column
              key={`session-${index}`}
              field={`session${index + 1}Sort`}
              title={`Session ${index + 1}`}
              width="120px"
              cells={{ data: (props) => <SessionStatusCell {...props} field={String(index + 1)} /> }}
            />
          ))}
        </Grid>
      </div>
    </div>
  );
};

export default Dashboard;
